import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

const PRIORITY_COLORS = {
  URGENT: { bg: '#FEE2E2', color: '#DC2626', label: 'Urgent' },
  HIGH: { bg: '#FFF7ED', color: '#EA580C', label: 'High' },
  MEDIUM: { bg: '#FEF9C3', color: '#CA8A04', label: 'Medium' },
  LOW: { bg: '#F3F4F6', color: '#6B7280', label: 'Low' },
};

const STATUS_LABELS = {
  PENDING: 'Pending',
  IN_PROGRESS: 'In Progress',
  RESOLVED: 'Resolved',
  ESCALATED_EXTERNAL: 'External',
};

const RESOLUTION_TYPES = [
  { value: 'PAYMENT_RECEIVED', label: 'Payment Received' },
  { value: 'PAYMENT_PLAN_CREATED', label: 'Payment Plan Created' },
  { value: 'BALANCE_ADJUSTED', label: 'Balance Adjusted' },
  { value: 'DISPUTE_RESOLVED', label: 'Dispute Resolved' },
  { value: 'SENT_TO_COLLECTIONS', label: 'Sent to Collections' },
  { value: 'WRITTEN_OFF', label: 'Written Off' },
];

function BillingEscalations({ practiceId }) {
  const [escalations, setEscalations] = useState([]);
  const [stats, setStats] = useState({});
  const [filter, setFilter] = useState({ status: '', priority: '' });
  const [loading, setLoading] = useState(true);
  const [resolveModal, setResolveModal] = useState(null);
  const [resolutionType, setResolutionType] = useState('');
  const [resolutionNotes, setResolutionNotes] = useState('');

  const loadData = useCallback(async () => {
    if (!practiceId) return;
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filter.status) params.append('status', filter.status);
      if (filter.priority) params.append('priority', filter.priority);

      const [escResult, statsResult] = await Promise.all([
        api.get(`/practices/${practiceId}/billing/escalations?${params}`),
        api.get(`/practices/${practiceId}/billing/escalations/stats`),
      ]);
      if (escResult.success) setEscalations(escResult.data || []);
      if (statsResult.success) setStats(statsResult.data || {});
    } catch (err) {
      console.error('Error loading escalations:', err);
    } finally {
      setLoading(false);
    }
  }, [practiceId, filter]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleAssign = async (id) => {
    try {
      await api.post(`/practices/${practiceId}/billing/escalations/${id}/assign`, {});
      loadData();
    } catch (err) {
      console.error('Error assigning escalation:', err);
    }
  };

  const handleResolve = async () => {
    if (!resolveModal || !resolutionType) return;
    try {
      await api.post(`/practices/${practiceId}/billing/escalations/${resolveModal}/resolve`, {
        resolutionType,
        notes: resolutionNotes,
      });
      setResolveModal(null);
      setResolutionType('');
      setResolutionNotes('');
      loadData();
    } catch (err) {
      console.error('Error resolving escalation:', err);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>Loading escalations...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '1.5rem' }}>
      <h1 style={{ fontSize: '1.5rem', fontWeight: 600, marginBottom: '1rem' }}>Billing Escalations</h1>

      {/* Stats Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        <StatCard label="Pending" value={stats.pending || 0} color="#CA8A04" />
        <StatCard label="In Progress" value={stats.in_progress || 0} color="#2563EB" />
        <StatCard label="Urgent Open" value={stats.urgent_open || 0} color="#DC2626" />
        <StatCard label="Open Balance" value={`$${(stats.open_balance || 0).toLocaleString()}`} color="#059669" />
        <StatCard label="Resolved" value={stats.resolved || 0} color="#6B7280" />
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
        <select value={filter.status} onChange={e => setFilter(f => ({ ...f, status: e.target.value }))}
          style={{ padding: '0.5rem', borderRadius: '6px', border: '1px solid #D1D5DB' }}>
          <option value="">All Statuses</option>
          <option value="PENDING">Pending</option>
          <option value="IN_PROGRESS">In Progress</option>
          <option value="RESOLVED">Resolved</option>
        </select>
        <select value={filter.priority} onChange={e => setFilter(f => ({ ...f, priority: e.target.value }))}
          style={{ padding: '0.5rem', borderRadius: '6px', border: '1px solid #D1D5DB' }}>
          <option value="">All Priorities</option>
          <option value="URGENT">Urgent</option>
          <option value="HIGH">High</option>
          <option value="MEDIUM">Medium</option>
          <option value="LOW">Low</option>
        </select>
      </div>

      {/* Table */}
      <div style={{ background: '#fff', borderRadius: '8px', border: '1px solid #E5E7EB', overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #E5E7EB', background: '#F9FAFB' }}>
              <th style={th}>Patient</th>
              <th style={th}>Reason</th>
              <th style={th}>Priority</th>
              <th style={th}>Balance</th>
              <th style={th}>Days Overdue</th>
              <th style={th}>Status</th>
              <th style={th}>Assigned To</th>
              <th style={th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {escalations.length === 0 ? (
              <tr><td colSpan={8} style={{ padding: '2rem', textAlign: 'center', color: '#6B7280' }}>No escalations found</td></tr>
            ) : escalations.map(esc => (
              <tr key={esc.id} style={{ borderBottom: '1px solid #E5E7EB' }}>
                <td style={td}>{esc.first_name} {esc.last_name}</td>
                <td style={td}>{esc.escalation_reason?.replace(/_/g, ' ')}</td>
                <td style={td}>
                  <span style={{
                    padding: '2px 8px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: 600,
                    background: PRIORITY_COLORS[esc.escalation_priority]?.bg,
                    color: PRIORITY_COLORS[esc.escalation_priority]?.color,
                  }}>
                    {PRIORITY_COLORS[esc.escalation_priority]?.label}
                  </span>
                </td>
                <td style={td}>${(esc.balance_amount || 0).toFixed(2)}</td>
                <td style={td}>{esc.days_overdue || 0}</td>
                <td style={td}>{STATUS_LABELS[esc.queue_status] || esc.queue_status}</td>
                <td style={td}>{esc.assigned_first ? `${esc.assigned_first} ${esc.assigned_last}` : '-'}</td>
                <td style={td}>
                  {esc.queue_status === 'PENDING' && (
                    <button onClick={() => handleAssign(esc.id)}
                      style={{ padding: '4px 12px', borderRadius: '4px', border: '1px solid #2563EB', color: '#2563EB', background: 'white', cursor: 'pointer', fontSize: '0.75rem', marginRight: '4px' }}>
                      Claim
                    </button>
                  )}
                  {['PENDING', 'IN_PROGRESS'].includes(esc.queue_status) && (
                    <button onClick={() => setResolveModal(esc.id)}
                      style={{ padding: '4px 12px', borderRadius: '4px', border: '1px solid #059669', color: '#059669', background: 'white', cursor: 'pointer', fontSize: '0.75rem' }}>
                      Resolve
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Resolve Modal */}
      {resolveModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: '12px', padding: '1.5rem', width: '400px', maxWidth: '90vw' }}>
            <h3 style={{ marginBottom: '1rem' }}>Resolve Escalation</h3>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Resolution Type</label>
            <select value={resolutionType} onChange={e => setResolutionType(e.target.value)}
              style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #D1D5DB', marginBottom: '1rem' }}>
              <option value="">Select...</option>
              {RESOLUTION_TYPES.map(rt => <option key={rt.value} value={rt.value}>{rt.label}</option>)}
            </select>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Notes</label>
            <textarea value={resolutionNotes} onChange={e => setResolutionNotes(e.target.value)}
              rows={3} style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #D1D5DB', marginBottom: '1rem', resize: 'vertical' }} />
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button onClick={() => { setResolveModal(null); setResolutionType(''); setResolutionNotes(''); }}
                style={{ padding: '8px 16px', borderRadius: '6px', border: '1px solid #D1D5DB', background: '#fff', cursor: 'pointer' }}>
                Cancel
              </button>
              <button onClick={handleResolve} disabled={!resolutionType}
                style={{ padding: '8px 16px', borderRadius: '6px', border: 'none', background: resolutionType ? '#059669' : '#D1D5DB', color: '#fff', cursor: resolutionType ? 'pointer' : 'default' }}>
                Resolve
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, color }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '1rem' }}>
      <div style={{ fontSize: '0.75rem', color: '#6B7280', marginBottom: '0.25rem' }}>{label}</div>
      <div style={{ fontSize: '1.5rem', fontWeight: 700, color }}>{value}</div>
    </div>
  );
}

const th = { padding: '0.75rem', textAlign: 'left', fontWeight: 600, fontSize: '0.75rem', color: '#6B7280' };
const td = { padding: '0.75rem' };

export default BillingEscalations;
